/* MARVI OCEAN — Blue Migration, phone-only folding.
   Added 2026-07-29.

   The page runs ~5,300px tall on a 390px screen. Four blocks account for most of it:
   the species chapters (~2,533px), the migration timelines (~1,665px), the "This Month"
   list (~825px) and the species picker (~446px). Below 700px each of those collapses
   behind a tap, taking the page to roughly 1,500–2,000px.

   Deliberate choices:
   - Phone only. Above 700px nothing is touched and every panel is forced back open,
     so rotating a tablet or resizing a desktop window can never leave content hidden.
   - Content stays in the DOM. Collapsed, not removed — Google indexes it normally, which
     matters because this is the site's strongest organic asset.
   - The chapter headings stay <h2>. The button goes INSIDE the heading rather than
     replacing it, so the document outline and the heading text are unchanged.
   - Every block is wired independently inside its own try/catch. This page's map, globe
     and timeline are driven by other scripts; if any one block here fails it must not
     take the rest of the page with it.
   - Nothing here binds to the existing accordion rows, which have their own handlers. */
(function () {
  'use strict';

  var MQ = window.matchMedia('(max-width:700px)');
  var built = false;
  var panels = [];          // every {btn, panel} pair we create, for the desktop re-open

  function labelBtn(text, expanded) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'bc-fold-btn';
    b.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    var s = document.createElement('span');
    s.className = 'bc-fold-txt';
    s.textContent = text;
    var i = document.createElement('span');
    i.className = 'bc-fold-ic';
    i.setAttribute('aria-hidden', 'true');
    b.appendChild(s);
    b.appendChild(i);
    return b;
  }

  /* The globe sizes itself from container.clientWidth and only re-fits on window
     resize, a panel transitionend, and timers at 150ms/600ms. This script runs later
     than all of those because it waits for JS-built content — so any layout change it
     makes would leave the globe's canvas at its old width and clipped down one side.
     Firing a resize event reuses the globe's own resizeToContainer() handler rather
     than reaching into its internals. Also covers the 2D Leaflet map, which listens
     for the same event. */
  var refitTimer = null;
  function refitMap() {
    clearTimeout(refitTimer);
    refitTimer = setTimeout(function () {
      try { window.dispatchEvent(new Event('resize')); } catch (e) {}
    }, 60);
  }

  function wire(btn, panel) {
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
      refitMap();
    });
    panels.push({ btn: btn, panel: panel });
  }

  /* ---- 1. species chapters — closed by default, count in the label ---- */
  function foldChapters() {
    document.querySelectorAll('#bcChapters .bc-chapter').forEach(function (ch) {
      var h2 = ch.querySelector('.bc-chapter-title');
      var grid = ch.querySelector('.bc-accordion');
      if (!h2 || !grid || h2.querySelector('.bc-fold-btn')) return;
      var n = grid.querySelectorAll('.bc-acc-item').length;
      if (!n) return;                                   // nothing built yet, leave it alone
      var btn = labelBtn(h2.textContent.trim() + ' (' + n + ')', false);
      h2.textContent = '';
      h2.appendChild(btn);
      h2.classList.add('bc-fold-h');
      grid.hidden = true;
      wire(btn, grid);
    });
  }

  /* ---- 2. migration timelines — the controls stay visible, only the rows fold ---- */
  function foldTimelines() {
    var rows = document.getElementById('bcSpeciesTimelines');
    if (!rows || rows.previousElementSibling && rows.previousElementSibling.classList.contains('bc-fold-btn')) return;
    var n = rows.children.length;
    if (!n) return;
    var btn = labelBtn('Show migration timelines (' + n + ')', false);
    btn.classList.add('bc-fold-btn--block');
    rows.parentNode.insertBefore(btn, rows);
    rows.hidden = true;
    wire(btn, rows);
    btn.addEventListener('click', function () {
      btn.querySelector('.bc-fold-txt').textContent =
        btn.getAttribute('aria-expanded') === 'true'
          ? 'Hide migration timelines'
          : 'Show migration timelines (' + n + ')';
    });
  }

  /* ---- 3. "This month" — first four visible, the rest behind a tap ----
     Partly shown rather than fully folded: it is the first thing under the hero, so
     showing nothing would read as a broken page, and showing all 44 is the wall
     between the visitor and the map. */
  function foldMonth() {
    var ul = document.getElementById('bcMonthList');
    if (!ul || ul.dataset.folded === '1') return;
    var items = ul.children;
    if (items.length <= 6) return;                      // short month, nothing to gain
    var hiddenCount = items.length - 4;
    for (var i = 4; i < items.length; i++) items[i].classList.add('bc-fold-hidden');
    var btn = labelBtn('Show all ' + items.length + ' species this month', false);
    btn.classList.add('bc-fold-btn--block');
    ul.parentNode.insertBefore(btn, ul.nextSibling);
    ul.dataset.folded = '1';
    btn.addEventListener('click', function () {
      var open = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      ul.classList.toggle('bc-fold-open', !open);
      btn.querySelector('.bc-fold-txt').textContent = open
        ? 'Show all ' + items.length + ' species this month'
        : 'Show fewer';
    });
    panels.push({ btn: btn, panel: null, list: ul, n: hiddenCount });
  }

  /* ---- 4. species picker — collapsed so the globe is reachable without scrolling past 44 rows ---- */
  function foldLegend() {
    var leg = document.getElementById('bcGlobeLegend');
    if (!leg || leg.previousElementSibling && leg.previousElementSibling.classList.contains('bc-fold-btn')) return;
    var n = leg.querySelectorAll('.bc-legend-item').length;
    if (!n) return;
    var btn = labelBtn('Choose species (' + n + ')', false);
    btn.classList.add('bc-fold-btn--block');
    leg.parentNode.insertBefore(btn, leg);
    leg.hidden = true;
    wire(btn, leg);
  }

  /* ---- 5. the Animate button follows the globe on phones ----
     #bcPlayBtn sits in the migration-timeline block, several hundred pixels below the
     map. On a phone that meant scrolling away from the globe to start the animation,
     which then played where you couldn't see it. Below 700px the button is MOVED into
     the "SHOWING <date>" bar directly above the globe.

     Moved rather than copied on purpose: a second button would need its own play/pause
     label kept in sync with the first, and the two would drift the moment either was
     pressed. One node means the existing handler and its ▶/⏸ label keep working
     untouched. The original parent and next sibling are remembered so going back above
     700px puts it exactly where it was. */
  var playBtn = null, playHome = null, playNext = null;

  function movePlay() {
    if (!playBtn) playBtn = document.getElementById('bcPlayBtn');
    var bar = document.getElementById('bcMapCurrentMonth');
    if (!playBtn || !bar) return;
    if (MQ.matches) {
      if (playBtn.parentNode !== bar) {
        if (!playHome) { playHome = playBtn.parentNode; playNext = playBtn.nextSibling; }
        bar.appendChild(playBtn);
        bar.classList.add('bc-mcm-has-play');
      }
    } else if (playHome && playBtn.parentNode !== playHome) {
      playHome.insertBefore(playBtn, playNext);
      bar.classList.remove('bc-mcm-has-play');
    }
  }

  /* ---- 6. a jump target must be un-folded BEFORE anything scrolls to it ----
     Regression fixed 2026-07-29, introduced by this file.

     bc-globe.js and bc-map2d.js both end their "Full species guide" handler with

         document.querySelector('[data-scard-key="…"]').scrollIntoView(…)

     scrollIntoView on an element inside a display:none ancestor is a SILENT no-op —
     no error, no movement. Once foldChapters() set `grid.hidden = true`, every
     species card lived inside a collapsed .bc-accordion, so on a phone the button
     looked completely dead. Desktop was unaffected: nothing is folded there.
     Measured before the fix: phone scrollY 0 -> 0, desktop 0 -> 3799.

     Fixed here rather than in bc-globe.js / bc-map2d.js because this file created
     the problem and those two are lazy-loaded from a CDN chain — a capture-phase
     listener on the document runs before their own target-phase handlers no matter
     when they finally load, so the target is laid out by the time they scroll.
     Keyed on data-key rather than the button id so both jump paths are covered. */
  function revealTarget(el) {
    for (var n = el; n && n !== document.body; n = n.parentElement) {
      if (!n.hidden) continue;
      n.hidden = false;
      for (var i = 0; i < panels.length; i++) {
        if (panels[i].panel !== n) continue;
        panels[i].btn.setAttribute('aria-expanded', 'true');
        var txt = panels[i].btn.querySelector('.bc-fold-txt');
        if (txt && /^Show migration timelines/.test(txt.textContent)) {
          txt.textContent = 'Hide migration timelines';
        }
      }
    }
  }

  document.addEventListener('click', function (e) {
    try {
      var btn = e.target && e.target.closest && e.target.closest('#bcGlobeJumpBtn');
      if (!btn) return;
      var key = btn.getAttribute('data-key');
      if (!key) return;
      /* matched by comparing attribute values rather than building a selector —
         the key comes from data and must never be concatenated into one */
      var all = document.querySelectorAll('[data-scard-key]');
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute('data-scard-key') === key) { revealTarget(all[i]); return; }
      }
    } catch (err) { /* never let this break a click */ }
  }, true);

  function build() {
    if (built) return;
    var ok = 0;
    [foldChapters, foldTimelines, foldMonth, foldLegend].forEach(function (fn) {
      try { fn(); ok++; } catch (e) { /* one block failing must not stop the others */ }
    });
    if (ok) built = true;
    try { movePlay(); } catch (e) {}
    refitMap();               /* the folding just changed the layout — let the globe re-fit */
  }

  /* Everything on this page is rendered by other scripts, so wait for real content
     rather than for the DOM. Give up after ~9s instead of polling forever. */
  function whenReady() {
    var tries = 0;
    var t = setInterval(function () {
      tries++;
      var hasRows = document.querySelectorAll('#bcChapters .bc-acc-item').length > 0;
      var hasMonth = (document.getElementById('bcMonthList') || { children: [] }).children.length > 0;
      if (hasRows || hasMonth || tries > 60) {
        clearInterval(t);
        if (MQ.matches) build();
      }
    }, 150);
  }

  function onViewportChange() {
    try { movePlay(); } catch (e) {}      /* runs in both directions — puts it back on desktop */
    if (MQ.matches) { build(); return; }
    /* Desktop: never leave anything collapsed. */
    panels.forEach(function (p) {
      if (p.panel) p.panel.hidden = false;
      if (p.list) p.list.classList.add('bc-fold-open');
      p.btn.setAttribute('aria-expanded', 'true');
    });
  }

  if (MQ.addEventListener) MQ.addEventListener('change', onViewportChange);
  else if (MQ.addListener) MQ.addListener(onViewportChange);

  if (document.readyState === 'complete') whenReady();
  else window.addEventListener('load', whenReady);
})();
